import { FC, memo, useEffect, useRef, useState } from "react";
import { observer } from "mobx-react";
import PlaygroundForm from "../pg-form/PlaygroundForm";
import { ChatRole, IChat } from "@components/pages/project/Playground/types";
import { useStore } from "@stores/root-store";
import { useForm } from "antd/es/form/Form";
import { io } from "socket.io-client";
import { baseSocketUrl, vercelEnv } from "@api/const";
import { FileV2 } from "../../../../../../types/common-interfaces";
import { isArray } from "lodash";
import { SubmissionStage } from "@components/pages/project/single-functions/cn-converter/types";
import { CreditLimitModal } from "@commonComponents/limit-warning-modal";

interface AdvisorFormProps {}

const CNConverterMain: FC<AdvisorFormProps> = () => {
  const socketRef = useRef(null);
  const [form] = useForm();
  const [url, setUrl] = useState("");
  const [isUrlBoxOpen, setIsUrlBoxOpen] = useState(false);
  const [chat, setChat] = useState<IChat[]>([]);
  const [conceptNoteUploaded, setConceptNoteUploaded] = useState(false);
  const [blockSubmission, setBlockSubmission] = useState(false);
  const [submissionStage, setSubmissionStage] = useState<SubmissionStage>(
    SubmissionStage.Idle,
  );

  const responseTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const responseStartedRef = useRef(false);

  // cn evaluator plugin
  const [CNConverter_section, CNConverter_setSection] = useState("");
  const [CNConverter_subsection, CNConverter_setSubsection] = useState("");

  const [selectedDocuments, setSelectedDocuments] = useState<FileV2[] | null>(
    null,
  );

  const {
    user: { getSingleProjectData, getProjectNameData, updateTokenConsumption },
    modelType: { getSelectedModelType },
  } = useStore(null);

  const projectName = getProjectNameData
    ? JSON.parse(getProjectNameData).project_name
    : localStorage.getItem("projectName");

  const [errorMessage, setErrorMessage] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [input, setInput] = useState("");
  const [isInsufficientCreditsModalOpen, setIsInsufficientCreditsModalOpen] =
    useState(false);

  const runSubmissionStages = async () => {
    try {
      if (!conceptNoteUploaded) {
        const uploadDuration =
          selectedDocuments && selectedDocuments[0]?.data
            ? Math.min(
                10000,
                Math.floor(selectedDocuments[0].data.byteLength / 1024) * 100,
              )
            : 2000;

        setSubmissionStage(SubmissionStage.Uploading);
        await sleepOrBail(uploadDuration);
      }

      setSubmissionStage(SubmissionStage.Analyzing);
      await sleepOrBail(3000 + Math.random() * 2000);

      setSubmissionStage(SubmissionStage.Preparing);
      await sleepOrBail(3000 + Math.random() * 2000);

      // Only set Generating if response hasn't already begun
      if (!responseStartedRef.current) {
        setSubmissionStage(SubmissionStage.Generating);
      }

      // Fail-safe timeout
      responseTimeoutRef.current = setTimeout(() => {
        if (!responseStartedRef.current) {
          stopGeneration({ invokedByUser: false });
          setErrorMessage(
            "Hmmm…something seems to have gone wrong. Please try resubmitting.",
          );
        }
      }, 240000);
    } catch (err) {
      console.warn("Stage runner aborted early:", err);
    }
  };

  const sleepOrBail = async (ms: number) => {
    return new Promise<void>((resolve, reject) => {
      const interval = setTimeout(() => {
        if (responseStartedRef.current) {
          reject("response started");
        } else {
          resolve();
        }
      }, ms);

      // If response starts mid-sleep, cancel early
      if (responseStartedRef.current) {
        clearTimeout(interval);
        reject("response started");
      }
    });
  };

  const getProjectData = async ({
    seedChat = false,
  }: { seedChat?: boolean } = {}) => {
    const data = JSON.stringify({
      functionality: "concept note converter",
      project_name: projectName,
    });

    await getSingleProjectData(data).then((state) => {
      if (state) {
        if (
          seedChat === true &&
          Array.isArray(state.conversation) &&
          state.conversation.length > 0
        ) {
          const fileName = state.conversation[0].content[0].text
            .split(":")[1]
            .trim();

          const sectionRegex = /section:\s*([^\n]*)/i;
          const subsectionRegex = /subsection:\s*([^\n]*)/i;

          setChat((prev) => {
            // remove the first message because its a filename
            const conversation = state.conversation.slice(1);

            const newChat = conversation.map((message, index) => {
              let foundSection = "";
              let foundSubsection = "";

              if (
                message.role === "user" &&
                message.content[0].text.includes("section:")
              ) {
                const sectionMatch =
                  message.content[0].text.match(sectionRegex);
                const subsectionMatch =
                  message.content[0].text.match(subsectionRegex);

                if (sectionMatch) {
                  foundSection = sectionMatch[1];
                }

                if (subsectionMatch && subsectionMatch[1] !== "None") {
                  foundSubsection = subsectionMatch[1];
                }
              }

              return {
                id: index + 1,
                role: message.role === "user" ? ChatRole.USER : ChatRole.CHAT,
                message: foundSection !== "" ? "" : message.content[0].text,
                isLastChatMessage: index === conversation.length - 1,
                section: foundSection,
                subsection: foundSubsection,
                url,
                files: message.role === "user" ? [fileName] : [],
              };
            });
            return [...prev, ...newChat];
          });

          const fileData = new ArrayBuffer(0);
          const dummyFile: FileV2 = {
            id: "1",
            filename: fileName,
            data: fileData,
          };
          setSelectedDocuments([dummyFile]);
          setConceptNoteUploaded(true);
        }
      }
    });
  };

  useEffect(() => {
    if (socketRef.current) {
      socketRef.current.disconnect();
    }

    const socket = io(baseSocketUrl, {
      transports: ["websocket", "polling"],
    });

    socketRef.current = socket;

    socket.on("connect", () => {
      console.log("Socket connected:", socket.id);
    });

    socket.on("token_limit_exceeded", (message) => {
      stopGeneration({ invokedByUser: false });

      setIsInsufficientCreditsModalOpen(true);
      setErrorMessage(
        "Insufficient credits. Please contact us to add more credits.",
      );
    });

    socket.on("answer", (answer) => {
      if (!responseStartedRef.current) {
        responseStartedRef.current = true;
        clearTimeout(responseTimeoutRef.current);
        responseTimeoutRef.current = null;

        setSubmissionStage(SubmissionStage.Idle);
      }

      const isStopGeneration = localStorage.getItem("isStopGeneration");

      if (!isSubmitting && answer) {
        setIsSubmitting(true);
      }

      if (isStopGeneration) {
        stopGeneration({ invokedByUser: true });
        return;
      }

      if (answer === "#####") {
        stopGeneration({ invokedByUser: false });
        return;
      }

      setChat((prev) => {
        const lastMessage = prev[prev.length - 1];

        const shouldCreateNew =
          !lastMessage || lastMessage.role === ChatRole.USER;

        if (shouldCreateNew) {
          const lastId = lastMessage ? lastMessage.id : 0;
          return [
            ...prev,
            {
              id: lastId + 1,
              role: ChatRole.CHAT,
              message: answer,
              isLastChatMessage: true,
              isGenerating: true,
            },
          ];
        }

        // Otherwise, append to the last message
        return prev.map((msg, index) => {
          const isLast = index === prev.length - 1;
          return {
            ...msg,
            ...(isLast
              ? {
                  message: msg.message + answer,
                  isLastChatMessage: true,
                  isGenerating: true,
                }
              : { isLastChatMessage: false }),
          };
        });
      });
    });

    socket.on("error_message", (err) => {
      stopGeneration({ invokedByUser: false });
      setErrorMessage(err);
    });

    socket.on("disconnect", () => {
      console.log("Socket disconnected");
      stopGeneration({ invokedByUser: false });
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  useEffect(() => {
    if (errorMessage) {
      setErrorMessage("");
    }
  }, [input, CNConverter_section, CNConverter_subsection]);

  const handleSubmit = async (
    values,
    regenConfig: {
      isRegen?: boolean;
      lastMessage?: any;
      configOnlyMessage?: boolean;
    } = {},
  ) => {
    setErrorMessage("");

    if (
      selectedDocuments === null ||
      (selectedDocuments.length === 0 && conceptNoteUploaded === false)
    ) {
      setErrorMessage("Please choose a concept note before submitting.");
      return;
    }

    const isSectionSelected =
      CNConverter_section !== "" || CNConverter_subsection !== "";
    const isInputFilled = input?.trim()?.length > 0;

    if (
      (isSectionSelected && isInputFilled) ||
      (!isSectionSelected && !isInputFilled)
    ) {
      setErrorMessage(
        "Please either ask a follow-up question or select a new section to analyze, not both.",
      );
      return;
    }

    if (blockSubmission) {
      setErrorMessage(
        "Please select a section and subsection before submitting.",
      );
      return;
    }

    const inputVal = regenConfig.isRegen === true ? values.q : input;
    setIsSubmitting(true);

    setChat((prev) =>
      prev.map((el) => {
        return { ...el, isLastChatMessage: false };
      }),
    );

    let message = {
      id: chat[chat.length - 1]?.id ? chat[chat.length - 1]?.id + 1 : 1,
      role: ChatRole.USER,
      message: inputVal,
      url,
      containsFile: selectedDocuments !== null,
      files:
        selectedDocuments !== null
          ? selectedDocuments?.map((e) => e.filename)
          : [],
      isLastChatMessage: false,
      section: "",
      subsection: "",
    };

    const email = localStorage.getItem("email");

    const data: any = {
      user_query: inputVal
        ? [
            {
              role: "user",
              content: [
                {
                  type: "text",
                  text: inputVal,
                },
              ],
            },
          ]
        : undefined,
      email,
      model: getSelectedModelType ?? "Titan Pro",
      project_name: projectName,
    };

    const socket = socketRef.current;

    if (!socket?.connected) {
      socket.connect();
    }

    if (conceptNoteUploaded !== true) {
      if (selectedDocuments?.length > 0) {
        data.concept_note = selectedDocuments[0];
      }
    }

    if (CNConverter_section !== "") {
      data.section = CNConverter_section;
      message.section = CNConverter_section;
    }

    if (CNConverter_subsection !== "") {
      data.subsection = CNConverter_subsection;
      message.subsection = CNConverter_subsection;
    }

    if (
      regenConfig.isRegen === true &&
      regenConfig.configOnlyMessage !== true
    ) {
      message = regenConfig.lastMessage;
    }

    setChat((prev) => [...prev, message]);
    console.log("data emitting,", data);

    responseStartedRef.current = false; // RESET for new submission
    socket.emit("concept_note_converter", data);
    // clear input field
    setInput("");

    // scroll to bottom on message
    const chatElem = document.getElementById("chat");
    setTimeout(() => {
      chatElem.scroll({
        top: chatElem.scrollHeight,
      });
    }, 150);

    CNConverter_setSection("");
    CNConverter_setSubsection("");

    runSubmissionStages();

    socketRef.current = socket;
    setIsUrlBoxOpen(false);
  };

  useEffect(() => {
    getProjectData({ seedChat: true });
  }, []);

  useEffect(() => {
    if (chat.length > 0 && chat[0].containsFile === true) {
      setConceptNoteUploaded(true);
    }
  }, [chat]);

  const stopGeneration = async ({
    invokedByUser = false,
  }: {
    invokedByUser?: boolean;
  }) => {
    responseStartedRef.current = true;

    if (responseTimeoutRef.current) {
      clearTimeout(responseTimeoutRef.current);
      responseTimeoutRef.current = null;
    }

    if (socketRef.current) {
      await socketRef.current.disconnect();
      await setIsSubmitting(false);
    }

    setSubmissionStage(SubmissionStage.Idle);

    updateTokenConsumption();

    if (invokedByUser) {
      const lastMessage = chat[chat.length - 1];
      if (lastMessage.role === ChatRole.CHAT && lastMessage.message === "") {
        lastMessage.message = "__INTERNAL_STOPPED__";
      }
      setChat((prev) => [...prev.slice(0, -1), lastMessage]);
    }

    localStorage.removeItem("isStopGeneration");
  };

  const handleRegenerate = () => {
    const lastMessage = { ...chat[chat.length - 2] };
    const question = lastMessage.message;
    setChat(chat.slice(0, -2));
    setInput(question);
    form.setFieldValue("q", "");

    const values = {
      q: question,
    };

    handleSubmit(values, { isRegen: true, lastMessage });
  };

  return (
    <div style={{ padding: "30px 52px 12px" }}>
      <PlaygroundForm
        form={form}
        plugins={{
          CNConverter: true,
        }}
        pluginHandlers={{
          CNConverter: {
            CNConverter_section,
            CNConverter_setSection,
            CNConverter_subsection,
            CNConverter_setSubsection,
            setBlockSubmission,
            conceptNoteUploaded,
          },
        }}
        submissionStage={submissionStage}
        isSubmitting={isSubmitting}
        handleSubmit={handleSubmit}
        handleRegenerate={handleRegenerate}
        setInput={(val: string) => setInput(val)}
        input={input}
        chat={chat}
        url={url}
        setUrl={setUrl}
        isUrlBoxOpen={isUrlBoxOpen}
        setIsUrlBoxOpen={setIsUrlBoxOpen}
        errorMessage={errorMessage}
        initialSystemMessage={<span>Hello! How Can I Help You Today?</span>}
        disabled={vercelEnv === "production"}
        inputPlaceholder={
          conceptNoteUploaded
            ? vercelEnv === "production"
              ? ""
              : "Enter your query here"
            : "Upload a Concept Note"
        }
        // DISABLE FOLLOW UP
        // disabled={!conceptNoteUploaded}
        // inputPlaceholder={
        //   conceptNoteUploaded
        //     ? "Enter your query here"
        //     : "Upload a Concept Note"
        // }
        selectedDocuments={selectedDocuments}
        setSelectedDocuments={setSelectedDocuments}
        required={false}
        stopGeneration={() => stopGeneration({ invokedByUser: true })}
      />
      <CreditLimitModal
        open={isInsufficientCreditsModalOpen}
        setOpen={setIsInsufficientCreditsModalOpen}
        functionality={"concept note converter"}
      />
    </div>
  );
};

export default memo(observer(CNConverterMain));
